/**
 * TeamMembersList (HOF-627 §12 T1) — the Team pane's content: the farm's members, its pending
 * invitations, and inviting someone.
 *
 * Controls follow the server's answer, never a client-side role check: ``can_manage`` and
 * ``assignable_roles`` come from the caller's seat (a manager is offered no owner role and no
 * control over an owner's seat; a support view gets none at all). Phone and email are shown,
 * never edited — they are each person's own login (§12 rule 1).
 *
 * Layout (settings refactor, after Linear / Vercel / Slack member lists): avatar, name with a
 * "(du)" marker and status, a secondary line; on the right the role — an inline select for a
 * seat this person may manage, a badge otherwise — and every other row action in a "…" menu.
 * Removing someone asks first, in a dialog that names the consequence. Pending invitations are
 * their own group; inviting is the pane's one primary action.
 *
 * A role is never chosen without what it means beside it (catalogue: ``roleDescriptions.*``):
 * the inline select lists each role with one line on its rights, and the invite dialog shows
 * every assignable role as a card with its description, all visible to compare at once.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import {
    Avatar,
    Badge,
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Input,
    RadioCardGroup,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SettingsPane,
    SettingsRow,
    SettingsSection,
} from '@hofsync/design-system';
import { useToast } from '../../contexts/ToastContext';
import { farmMemberKeys, useFarmMembers } from './useFarmMembers';
import {
    LABOUR_SEAT_ROLES,
    ROLE_DESCRIPTION_FALLBACKS,
    ROLE_LABEL_FALLBACKS,
    TEAM_ROLES,
    inviteToFarm,
    isLabourSeatRole,
    isManagedSeat,
    memberDisplayName,
    refusalCode,
    removeMember,
    renewInvitation,
    revokeInvitation,
    roleDescriptionKey,
    roleLabelKey,
    updateInvitation,
    updateMember,
    type FarmMembersResponse,
    type InvitationPatch,
    type LabourSeatRole,
    type MemberPatch,
    type SeatRole,
    type TeamInvitation,
    type TeamMember,
    type TeamRole,
} from './farmMembers';
import type { SettingsPaneChrome } from './paneChrome';

function formatDate(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

export interface TeamMembersListProps extends SettingsPaneChrome {
    /** Further sections at the end of the pane (the legacy invite form). */
    children?: ReactNode;
}

export default function TeamMembersList({ backLink, focusOnMount, children }: TeamMembersListProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useFarmMembers();
    const [removing, setRemoving] = useState<TeamMember | null>(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [destination, setDestination] = useState('');
    const [inviteRole, setInviteRole] = useState<TeamRole>('staff');
    const [editingInvite, setEditingInvite] = useState<{ id: string; destination: string; role: TeamRole } | null>(null);

    const refresh = () => queryClient.invalidateQueries({ queryKey: farmMemberKeys.all });
    const refused = (error: unknown) => {
        const code = refusalCode(error);
        const message =
            code === 'last_owner'
                ? t('settings.team.lastOwner', 'Der Betrieb braucht mindestens einen Inhaber.')
                : code === 'seat_managed_by_labour_sync'
                  ? t(
                        'settings.team.managedByLabourRefused',
                        'Dieser Zugang folgt der Mitarbeiterkarte und wird über die Arbeit-App abgeglichen.'
                    )
                : code === 'conflict'
                  ? t('settings.team.alreadyInvited', 'Diese Person ist bereits eingeladen.')
                  : code === 'account_inactive'
                    ? t('settings.team.accountInactive', 'Das Konto dieser Person ist deaktiviert oder gelöscht.')
                    : t('settings.team.saveFailed', 'Die Änderung konnte nicht gespeichert werden.');
        showToast(message, 'error');
    };

    const patch = useMutation({
        mutationFn: ({ userId, change }: { userId: string; change: MemberPatch }) => updateMember(userId, change),
        onSuccess: refresh,
        onError: refused,
    });
    const remove = useMutation({
        mutationFn: (userId: string) => removeMember(userId),
        onSuccess: () => {
            setRemoving(null);
            refresh();
        },
        onError: (error) => {
            setRemoving(null);
            refused(error);
        },
    });
    const invite = useMutation({
        mutationFn: () => inviteToFarm(destination.trim(), inviteRole),
        onSuccess: () => {
            setDestination('');
            setInviteOpen(false);
            showToast(t('settings.team.saved', 'Gespeichert'), 'success');
            refresh();
        },
        onError: refused,
    });
    const revoke = useMutation({
        mutationFn: (inviteId: string) => revokeInvitation(inviteId),
        onSuccess: refresh,
        onError: refused,
    });
    const editInvite = useMutation({
        mutationFn: ({ inviteId, change }: { inviteId: string; change: InvitationPatch }) => updateInvitation(inviteId, change),
        onSuccess: () => {
            setEditingInvite(null);
            refresh();
        },
        onError: refused,
    });
    const renew = useMutation({
        mutationFn: (inviteId: string) => renewInvitation(inviteId),
        onSuccess: () => {
            showToast(t('settings.team.renewed', 'Einladung erneuert — sag der Person Bescheid.'), 'success');
            refresh();
        },
        onError: refused,
    });

    const title = t('settings.team.title', 'Team');
    const pane = (content: ReactNode, actions?: ReactNode) => (
        <SettingsPane
            title={title}
            description={t('settings.team.subtitle', 'Die Personen mit Zugang zu deinem Betrieb.')}
            backLink={backLink}
            focusOnMount={focusOnMount}
            actions={actions}
        >
            {content}
            {children}
        </SettingsPane>
    );

    if (isLoading) {
        return pane(<p role="status">{t('settings.team.loading', 'Team wird geladen …')}</p>);
    }
    if (isError || !data) {
        return pane(<p role="alert">{t('settings.team.loadFailed', 'Das Team konnte nicht geladen werden.')}</p>);
    }

    const roleLabel = (role: SeatRole) => t(roleLabelKey(role), ROLE_LABEL_FALLBACKS[role]);
    const roleDescription = (role: TeamRole) => t(roleDescriptionKey(role), ROLE_DESCRIPTION_FALLBACKS[role]);
    const assignable = data.assignable_roles;
    // A worker seat follows the employee card (HOF-696 D1): shown, never managed here.
    const mayManage = (member: TeamMember) =>
        data.can_manage && !isManagedSeat(member) && (member.role !== 'owner' || assignable.includes('owner'));
    // …except which labour seat it is: Arbeitskraft ⇄ Vorarbeiter (HOF-697).
    const mayChangeLabourRole = (member: TeamMember) =>
        data.can_manage && isLabourSeatRole(member.role);
    const destinationName = t('settings.team.invitePlaceholder', 'Telefon oder E-Mail');

    // HOF-708: support manages the team as the viewed person can — the backend decides on their seat.
    const actions = data.can_invite ? (
        <Button onClick={() => setInviteOpen(true)}>{t('settings.team.inviteOpen', 'Mitglied einladen')}</Button>
    ) : undefined;

    return pane(
        <>
            <SettingsSection
                title={t('settings.team.membersTitle', 'Mitglieder')}
                description={
                    data.can_manage
                        ? t(
                              'settings.team.selfServiceHint',
                              'Telefon und E-Mail ändert jede Person selbst in ihren eigenen Einstellungen.'
                          )
                        : t('settings.team.readOnlyHint', 'Nur Inhaber und Betriebsleitung können das Team verwalten.')
                }
                list
                listLabel={title}
            >
                {data.members.map((member) => (
                    <MemberRow
                        key={member.user_id}
                        member={member}
                        manageable={mayManage(member)}
                        labourRoleEditable={mayChangeLabourRole(member)}
                        assignable={assignable}
                        roleLabel={roleLabel}
                        onRole={(role) => patch.mutate({ userId: member.user_id, change: { role } })}
                        onStatus={(status) => patch.mutate({ userId: member.user_id, change: { status } })}
                        onLabel={(label) => patch.mutate({ userId: member.user_id, change: { display_label: label } })}
                        onRemove={() => setRemoving(member)}
                    />
                ))}
            </SettingsSection>

            {data.invitations.length > 0 && (
                <SettingsSection
                    title={t('settings.team.invitationsTitle', 'Ausstehende Einladungen')}
                    list
                    listLabel={t('settings.team.invitationsTitle', 'Ausstehende Einladungen')}
                >
                    {data.invitations.map((invitation) => (
                        <InvitationRow
                            key={invitation.invite_id}
                            invitation={invitation}
                            manageable={
                                // An owner invitation is only an owner's to change (as an owner's seat is).
                                data.can_invite &&
                                invitation.source === 'farm' &&
                                (invitation.role !== 'owner' || assignable.includes('owner'))
                            }
                            assignable={assignable}
                            roleLabel={roleLabel}
                            editing={editingInvite?.id === invitation.invite_id ? editingInvite : null}
                            onEdit={() =>
                                setEditingInvite({ id: invitation.invite_id, destination: invitation.destination, role: invitation.role })
                            }
                            onDraft={(draft) => setEditingInvite({ id: invitation.invite_id, ...draft })}
                            onCancel={() => setEditingInvite(null)}
                            onSave={(draft) => {
                                const change: InvitationPatch = {};
                                if (draft.destination.trim() !== invitation.destination) change.destination = draft.destination.trim();
                                if (draft.role !== invitation.role) change.role = draft.role;
                                if (Object.keys(change).length) editInvite.mutate({ inviteId: invitation.invite_id, change });
                                else setEditingInvite(null);
                            }}
                            onRenew={() => renew.mutate(invitation.invite_id)}
                            onRevoke={() => revoke.mutate(invitation.invite_id)}
                        />
                    ))}
                </SettingsSection>
            )}

            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogContent closeLabel={t('settings.team.cancel', 'Abbrechen')}>
                    <DialogHeader>
                        <DialogTitle>{t('settings.team.inviteOpen', 'Mitglied einladen')}</DialogTitle>
                        <DialogDescription>
                            {t(
                                'settings.team.inviteDescription',
                                'Die Person bekommt Zugang zu diesem Betrieb, sobald sie sich mit dieser Nummer oder Adresse anmeldet.'
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <form
                        id="team-invite-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (destination.trim() && !invite.isPending) invite.mutate();
                        }}
                    >
                        <SettingsRow
                            stacked
                            label={destinationName}
                            htmlFor="team-invite-destination"
                            description={
                                destination.includes('@')
                                    ? // A HofSync account needs a phone: an e-mail invitation reaches only someone who
                                      // already signs in with that verified address (HOF-627, codex P2).
                                      t(
                                          'settings.team.emailInviteHint',
                                          'Eine Einladung per E-Mail erreicht nur Personen, die HofSync schon mit dieser bestätigten Adresse nutzen. Neue Personen bitte per Telefon einladen.'
                                      )
                                    : undefined
                            }
                        >
                            <Input
                                id="team-invite-destination"
                                value={destination}
                                onChange={(e) => setDestination(e.target.value)}
                                placeholder={destinationName}
                                autoComplete="off"
                            />
                        </SettingsRow>
                        {/* Every role visible with what it may do, to compare before choosing. */}
                        <RadioCardGroup
                            legend={t('settings.team.inviteRole', 'Rolle für die Einladung')}
                            name="team-invite-role"
                            value={inviteRole}
                            onValueChange={(role) => setInviteRole(role as TeamRole)}
                            options={TEAM_ROLES.filter((role) => assignable.includes(role)).map((role) => ({
                                value: role,
                                label: roleLabel(role),
                                description: roleDescription(role),
                            }))}
                        />
                    </form>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setInviteOpen(false)}>
                            {t('settings.team.cancel', 'Abbrechen')}
                        </Button>
                        <Button type="submit" form="team-invite-form" disabled={!destination.trim() || invite.isPending}>
                            {t('settings.team.invite', 'Einladen')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
                <DialogContent closeLabel={t('settings.team.cancel', 'Abbrechen')}>
                    <DialogHeader>
                        <DialogTitle>
                            {t('settings.team.removeTitle', '{{name}} aus dem Betrieb entfernen?', {
                                name: removing ? memberDisplayName(removing) : '',
                            })}
                        </DialogTitle>
                        <DialogDescription>
                            {t(
                                'settings.team.removeDescription',
                                'Die Person verliert sofort den Zugang zu diesem Betrieb. Ihr eigenes Konto bleibt bestehen.'
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setRemoving(null)}>
                            {t('settings.team.cancel', 'Abbrechen')}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={remove.isPending}
                            onClick={() => removing && remove.mutate(removing.user_id)}
                        >
                            {t('settings.team.remove', 'Entfernen')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>,
        actions
    );
}

interface RoleSelectProps {
    value: TeamRole;
    /** The seat's role as stored — listed (disabled) even when this person may not assign it. */
    current?: TeamRole;
    assignable: FarmMembersResponse['assignable_roles'];
    label: string;
    onChange: (role: TeamRole) => void;
}

/**
 * A role chosen inline at the end of a row: closed, it shows the role's name; open, each role
 * says in one line what it may do (DS `Select` item descriptions), so nobody assigns a role
 * without seeing what it means.
 */
function RoleSelect({ value, current = value, assignable, label, onChange }: RoleSelectProps) {
    const { t } = useTranslation();
    return (
        <Select value={value} onValueChange={(role) => onChange(role as TeamRole)}>
            <SelectTrigger aria-label={label} width="fit" controlSize="sm">
                <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
                {TEAM_ROLES.filter((role) => assignable.includes(role) || role === current).map((role) => (
                    <SelectItem
                        key={role}
                        value={role}
                        disabled={!assignable.includes(role)}
                        description={t(roleDescriptionKey(role), ROLE_DESCRIPTION_FALLBACKS[role])}
                    >
                        {t(roleLabelKey(role), ROLE_LABEL_FALLBACKS[role])}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/**
 * HOF-697 — the one control on a labour seat: Arbeitskraft or Vorarbeiter. Everything else about
 * the seat follows the employee card (the labour access sync), so nothing else is offered.
 */
function LabourRoleSelect({ value, label, onChange }: { value: LabourSeatRole; label: string; onChange: (role: LabourSeatRole) => void }) {
    const { t } = useTranslation();
    return (
        <Select value={value} onValueChange={(role) => onChange(role as LabourSeatRole)}>
            <SelectTrigger aria-label={label} width="fit" controlSize="sm">
                <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
                {LABOUR_SEAT_ROLES.map((role) => (
                    <SelectItem key={role} value={role} description={t(roleDescriptionKey(role), ROLE_DESCRIPTION_FALLBACKS[role])}>
                        {t(roleLabelKey(role), ROLE_LABEL_FALLBACKS[role])}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

interface MemberRowProps {
    member: TeamMember;
    manageable: boolean;
    /** A labour seat whose Arbeitskraft ⇄ Vorarbeiter switch this viewer may use (HOF-697). */
    labourRoleEditable?: boolean;
    assignable: FarmMembersResponse['assignable_roles'];
    roleLabel: (role: SeatRole) => string;
    onRole: (role: TeamRole | LabourSeatRole) => void;
    onStatus: (status: 'active' | 'suspended') => void;
    onLabel: (label: string | null) => void;
    onRemove: () => void;
}

function MemberRow({
    member,
    manageable,
    labourRoleEditable = false,
    assignable,
    roleLabel,
    onRole,
    onStatus,
    onLabel,
    onRemove,
}: MemberRowProps) {
    const { t } = useTranslation();
    const [label, setLabel] = useState<string | null>(null); // the open draft, or null while closed
    const labelName = t('settings.team.labelPlaceholder', 'Bezeichnung im Betrieb');
    const name = memberDisplayName(member);
    const contact = [member.phone, member.email].filter(Boolean).join(' · ');

    return (
        <SettingsRow
            as="li"
            media={<Avatar name={name} />}
            label={
                <>
                    {name}
                    {member.is_you && ` ${t('settings.team.you', '(du)')}`}{' '}
                    {member.status === 'suspended' && <Badge tone="warning">{t('settings.team.suspended', 'Pausiert')}</Badge>}
                </>
            }
            description={[
                member.display_label,
                contact,
                isManagedSeat(member)
                    ? t('settings.team.managedByLabour', 'Zugang folgt der Mitarbeiterkarte (Arbeit-App)')
                    : null,
            ]
                .filter(Boolean)
                .join(' · ')}
        >
            {manageable && label !== null ? (
                // The farm-local name (§12 rule 1): the person's own first/last name stays theirs.
                <>
                    <Input
                        value={label}
                        maxLength={100}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder={labelName}
                        aria-label={labelName}
                        autoFocus
                    />
                    <Button
                        size="sm"
                        onClick={() => {
                            onLabel(label.trim() || null);
                            setLabel(null);
                        }}
                    >
                        {t('settings.team.save', 'Speichern')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setLabel(null)}>
                        {t('settings.team.cancel', 'Abbrechen')}
                    </Button>
                </>
            ) : manageable ? (
                <>
                    <RoleSelect
                        value={member.role as TeamRole}
                        assignable={assignable}
                        label={t('settings.team.roleLabel', 'Rolle')}
                        onChange={onRole}
                    />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                // Composed, not interpolated: the string is a locator, and a ``{{name}}``
                                // placeholder resolves differently under i18next and a test's ``t`` stub.
                                aria-label={`${t('settings.team.actionsFor', 'Aktionen für')} ${name}`}
                            >
                                <MoreHorizontal aria-hidden="true" size={16} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuItem onSelect={() => setLabel(member.display_label ?? '')}>
                                {t('settings.team.editLabel', 'Bezeichnung')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onStatus(member.status === 'suspended' ? 'active' : 'suspended')}>
                                {member.status === 'suspended'
                                    ? t('settings.team.activate', 'Aktivieren')
                                    : t('settings.team.suspend', 'Pausieren')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem tone="danger" onSelect={onRemove}>
                                {t('settings.team.remove', 'Entfernen')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            ) : labourRoleEditable && isLabourSeatRole(member.role) ? (
                <LabourRoleSelect value={member.role} label={t('settings.team.roleLabel', 'Rolle')} onChange={onRole} />
            ) : (
                <Badge>{roleLabel(member.role)}</Badge>
            )}
        </SettingsRow>
    );
}

interface InvitationDraft {
    destination: string;
    role: TeamRole;
}

interface InvitationRowProps {
    invitation: TeamInvitation;
    manageable: boolean;
    assignable: FarmMembersResponse['assignable_roles'];
    roleLabel: (role: TeamRole) => string;
    editing: InvitationDraft | null;
    onEdit: () => void;
    onDraft: (draft: InvitationDraft) => void;
    onCancel: () => void;
    onSave: (draft: InvitationDraft) => void;
    onRenew: () => void;
    onRevoke: () => void;
}

function InvitationRow({
    invitation,
    manageable,
    assignable,
    roleLabel,
    editing,
    onEdit,
    onDraft,
    onCancel,
    onSave,
    onRenew,
    onRevoke,
}: InvitationRowProps) {
    const { t } = useTranslation();
    const destinationName = t('settings.team.invitePlaceholder', 'Telefon oder E-Mail');
    const details = [
        roleLabel(invitation.role),
        t('settings.team.invitationExpires', 'gültig bis {{date}}', { date: formatDate(invitation.expires_at) }),
        invitation.channel === 'email' ? t('settings.team.emailInvitation', 'nur für bestehende HofSync-Konten') : null,
        invitation.source === 'legacy'
            ? t('settings.team.legacyInvitation', 'Alte Einladung (im Einladungsformular verwalten)')
            : null,
    ];

    return (
        <SettingsRow
            as="li"
            media={<Avatar name={invitation.destination} />}
            label={invitation.destination}
            description={details.filter(Boolean).join(' · ')}
        >
            {manageable && editing ? (
                // A pending invitation's destination is the farm's until accepted (§12 rule 1).
                <>
                    <Input
                        value={editing.destination}
                        onChange={(e) => onDraft({ ...editing, destination: e.target.value })}
                        placeholder={destinationName}
                        aria-label={destinationName}
                    />
                    <RoleSelect
                        value={editing.role}
                        current={invitation.role}
                        assignable={assignable}
                        label={t('settings.team.inviteRole', 'Rolle für die Einladung')}
                        onChange={(role) => onDraft({ ...editing, role })}
                    />
                    <Button size="sm" disabled={!editing.destination.trim()} onClick={() => onSave(editing)}>
                        {t('settings.team.save', 'Speichern')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onCancel}>
                        {t('settings.team.cancel', 'Abbrechen')}
                    </Button>
                </>
            ) : (
                <>
                    <Badge tone="info">
                        {invitation.source === 'legacy'
                            ? t('settings.team.legacyBadge', 'Alte Einladung')
                            : t('settings.team.invitationPending', 'Einladung ausstehend')}
                    </Badge>
                    {manageable && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`${t('settings.team.invitationActionsFor', 'Aktionen für die Einladung an')} ${invitation.destination}`}
                                >
                                    <MoreHorizontal aria-hidden="true" size={16} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onSelect={onEdit}>{t('settings.team.editInvitation', 'Ändern')}</DropdownMenuItem>
                                <DropdownMenuItem onSelect={onRenew}>{t('settings.team.renewInvitation', 'Erneuern')}</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem tone="danger" onSelect={onRevoke}>
                                    {t('settings.team.revoke', 'Zurückziehen')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </>
            )}
        </SettingsRow>
    );
}
